import { Helmet } from 'react-helmet-async'
import { Link } from '../navigation'
import { useTranslation } from 'react-i18next'
import _, { Dictionary } from 'lodash'

import { useFilteredPatents } from '../filter/FilteredPatents'
import { useFilteredTrademarks } from '../filter/FilteredTrademarks'
import { useFilteredDesigns } from '../filter/FilteredDesigns'
import { useFilteredExternal } from '../filter/FilteredExternal'
import { useProducts } from '../products/ProductsProvider'

import { SortButton } from '../patents/datawizard/DataWizard'
import { DwSettingsProvider, useDwSettingsProvider } from '../patents/datawizard/DwSettings'
import { QueriesProvider, QueryDeleteModal, QueryEditorModal, QueryMenu, QueryOpenModal, useQueriesProvider } from '../patents/datawizard/Queries'
import { AnnuityFields, AnnuityFieldsProvider, useAnnuityFields } from '../patents/datawizard/AnnuityFields'
import { calculateTotalRows } from '../trademarks/datawizard'
import TagList from '../components/TagList'
import LoadingButton from '../components/button/LoadingButton'
import { createExcelFile } from '../backend'
import { useAuth, useRoles } from '../user/Auth'
import { useSingleTeamMangement } from '../Management'
import { useIncome } from '../income/IncomeProvider'
import { useFxContext } from '../forecast/FxProvider'

import { Family, Member } from '../patents/patents'
import { Trademark, TrademarkFamily } from '../trademarks/TrademarksProvider'
import { DesignFamily, DesignRegistration } from '../designs/designs'
import { CompetitorFamily, CompetitorMember } from '../external/external'
import { Commodity } from '../products/products'
import { Invention, useInventions } from '../inventions/InventionsProvider'

import { familyUrl, memberUrl } from '../patents/utils'
import { trademarkUrl, trademarkFamilyUrl } from '../trademarks/utils'
import { designRegistrationUrl, designFamilyUrl } from '../designs/utils'
import { competitorFamilyUrl, competitorMemberUrl } from '../external/utils'
import { commodityReferenceUrl } from '../products/products'
import { ip_management } from '../data'
import IpManagementBreadcrumbs from './IpManagementBreadcrumbs'

// ─── Group-by constants ──────────────────────────────────────────────────────

const GRP_PATENT_MEMBER = 'patents:family-member'
const GRP_PATENT_FAMILY = 'patents:family'
const GRP_TRADEMARK = 'trademarks:trademark'
const GRP_TRADEMARK_FAMILY = 'trademarks:family'
const GRP_DESIGN = 'designs:design-registration'
const GRP_DESIGN_FAMILY = 'designs:family'
const GRP_COMPETITOR_MEMBER = 'competitors:competitor-member'
const GRP_COMPETITOR_FAMILY = 'competitors:family'
const GRP_PRODUCT = 'products:product'

const ALL_GROUP_BY = [
    GRP_PATENT_MEMBER, GRP_PATENT_FAMILY,
    GRP_TRADEMARK, GRP_TRADEMARK_FAMILY,
    GRP_DESIGN, GRP_DESIGN_FAMILY,
    GRP_COMPETITOR_MEMBER, GRP_COMPETITOR_FAMILY,
    GRP_PRODUCT,
]

// ─── Internal field-group keys (stable, not translated) ──────────────────────

const PATENT_FAMILY_GRP = 'p-family'
const PATENT_MEMBER_GRP = 'p-member'
const TM_GRP = 'tm'
const TM_FAMILY_GRP = 'tm-family'
const DESIGN_GRP = 'design'
const DESIGN_FAMILY_GRP = 'design-family'
const COMP_GRP = 'comp'
const COMP_FAMILY_GRP = 'comp-family'
const PRODUCT_GRP = 'product'
const INVENTION_GRP = 'p-inventions'

const ANN_FIELD_KEYS = [
    'ann_cost-centers', 'ann_fee-due', 'ann_fees',
    'ann_instruction-due', 'ann_last-instruction', 'ann_renewals-status',
] as const

const currencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ─── Row type ────────────────────────────────────────────────────────────────

type IpRow = {
    id: string | number
    families: Family[]
    members: Member[]
    tmFamilies: TrademarkFamily[]
    trademarks: Trademark[]
    designFamilies: DesignFamily[]
    designRegistrations: DesignRegistration[]
    compFamilies: CompetitorFamily[]
    compMembers: CompetitorMember[]
    products: Commodity[]
    inventions: Invention[]
}

const emptyRow: Omit<IpRow, 'id'> = {
    families: [], members: [],
    tmFamilies: [], trademarks: [],
    designFamilies: [], designRegistrations: [],
    compFamilies: [], compMembers: [],
    products: [],
    inventions: [],
}

// ─── Row computation ─────────────────────────────────────────────────────────

function useIpRows(groupBy: string): IpRow[] {
    const { families, members, membersByFamilyId, familyById } = useFilteredPatents()
    const { trademarks, trademarkFamilies, trademarksByFamilyId, trademarkFamilyById } = useFilteredTrademarks()
    const { designRegistrations, designFamilies, familyById: designFamilyById, registrationsByFamilyId } = useFilteredDesigns()
    const { members: compMembers, families: compFamilies, familyById: compFamilyById, membersByFamilyId: compMembersByFamilyId } = useFilteredExternal()
    const { commodities } = useProducts()
    const { inventionsByMemberId } = useInventions()

    switch (groupBy) {
        case GRP_PATENT_MEMBER:
            return members.map(m => ({
                ...emptyRow,
                id: m.familyMemberId,
                families: [familyById[m.patentFamilyId]].filter(Boolean),
                members: [m],
                inventions: inventionsByMemberId[m.familyMemberId ?? -1] ?? [],
            }))
        case GRP_PATENT_FAMILY:
            return families.map(f => ({
                ...emptyRow,
                id: f.patentFamilyId,
                families: [f],
                members: membersByFamilyId[f.patentFamilyId] ?? [],
                inventions: (membersByFamilyId[f.patentFamilyId] ?? []).flatMap(m => inventionsByMemberId[m.familyMemberId ?? -1] ?? []),
            }))
        case GRP_TRADEMARK:
            return trademarks.map(tm => ({
                ...emptyRow,
                id: tm.trademarkId ?? 0,
                trademarks: [tm],
                tmFamilies: [trademarkFamilyById[tm.familyId]].filter(Boolean),
            }))
        case GRP_TRADEMARK_FAMILY:
            return trademarkFamilies.map(f => ({
                ...emptyRow,
                id: f.familyId ?? 0,
                tmFamilies: [f],
                trademarks: trademarksByFamilyId[f.familyId] ?? [],
            }))
        case GRP_DESIGN:
            return designRegistrations.map(r => ({
                ...emptyRow,
                id: r.registrationId ?? 0,
                designRegistrations: [r],
                designFamilies: [designFamilyById[r.familyId]].filter(Boolean),
            }))
        case GRP_DESIGN_FAMILY:
            return designFamilies.map(f => ({
                ...emptyRow,
                id: f.designFamilyId ?? 0,
                designFamilies: [f],
                designRegistrations: registrationsByFamilyId[f.designFamilyId] ?? [],
            }))
        case GRP_COMPETITOR_MEMBER:
            return compMembers.map(m => ({
                ...emptyRow,
                id: m.competitorMemberId ?? 0,
                compMembers: [m],
                compFamilies: [compFamilyById[m.competitorFamilyId]].filter(Boolean),
            }))
        case GRP_COMPETITOR_FAMILY:
            return compFamilies.map(f => ({
                ...emptyRow,
                id: f.competitorFamilyId ?? 0,
                compFamilies: [f],
                compMembers: compMembersByFamilyId[f.competitorFamilyId] ?? [],
            }))
        case GRP_PRODUCT:
            return commodities.map(c => ({
                ...emptyRow,
                id: c.commodityId,
                products: [c],
            }))
        default:
            return []
    }
}

// ─── Field definitions ───────────────────────────────────────────────────────

const _searchableFields = {
    [PATENT_FAMILY_GRP]: {
        p_familyRef:    (f: Family) => f.internalReference,
        p_familyName:   (f: Family) => f.familyName,
        p_priorityDate: (f: Family) => (f as any).priorityDate,
    },
    [PATENT_MEMBER_GRP]: {
        p_memberRef:     (m: Member) => m.internalReference,
        p_countryCode:   (m: Member) => m.countryCode,
        p_ipType:        (m: Member) => m.ipType,
        p_status:        (m: Member) => m.familyMemberStatus,
        p_appDate:       (m: Member) => m.applicationDate,
        p_appNumber:     (m: Member) => m.applicationNumber,
        p_patentDate:    (m: Member) => m.patentDate,
        p_patentNumber:  (m: Member) => m.patentNumber,
        p_expiryDate:    (m: Member) => m.expiryDate,
    },
    [TM_GRP]: {
        tm_reference:   (tm: Trademark) => tm.reference,
        tm_words:       (tm: Trademark) => tm.words,
        tm_countryCode: (tm: Trademark) => tm.countryCode,
        tm_brandType:   (tm: Trademark) => tm.brandType,
        tm_status:      (tm: Trademark) => tm.status,
        tm_appDate:     (tm: Trademark) => tm.applicationDate,
        tm_appNumber:   (tm: Trademark) => tm.applicationNumber,
        tm_regDate:     (tm: Trademark) => tm.registrationDate,
        tm_regNumber:   (tm: Trademark) => tm.registrationNumber,
        tm_renewalDate: (tm: Trademark) => tm.renewalDate,
    },
    [TM_FAMILY_GRP]: {
        tm_familyRef:  (f: TrademarkFamily) => f.reference,
        tm_familyName: (f: TrademarkFamily) => f.name,
    },
    [DESIGN_GRP]: {
        d_reference:   (r: DesignRegistration) => r.reference,
        d_countryCode: (r: DesignRegistration) => r.countryCode,
        d_designType:  (r: DesignRegistration) => r.designType,
        d_status:      (r: DesignRegistration) => r.status,
        d_appDate:     (r: DesignRegistration) => r.applicationDate,
        d_appNumber:   (r: DesignRegistration) => r.applicationNumber,
        d_regDate:     (r: DesignRegistration) => r.registrationDate,
        d_regNumber:   (r: DesignRegistration) => r.registrationNumber,
        d_renewalDate: (r: DesignRegistration) => r.renewalDate,
        d_expiryDate:  (r: DesignRegistration) => r.expiryDate,
    },
    [DESIGN_FAMILY_GRP]: {
        d_familyRef:  (f: DesignFamily) => f.reference,
        d_familyName: (f: DesignFamily) => f.name,
    },
    [COMP_GRP]: {
        comp_reference:  (m: CompetitorMember) => m.internalReference,
        comp_countryCode:(m: CompetitorMember) => m.countryCode,
        comp_ipType:     (m: CompetitorMember) => m.ipType,
        comp_status:     (m: CompetitorMember) => m.competitorMemberStatus,
        comp_appNumber:  (m: CompetitorMember) => m.applicationNumber,
        comp_patentNumber:(m: CompetitorMember) => m.patentNumber,
        comp_expiryDate: (m: CompetitorMember) => m.expiryDate,
    },
    [COMP_FAMILY_GRP]: {
        comp_familyRef:  (f: CompetitorFamily) => f.internalReference,
        comp_familyName: (f: CompetitorFamily) => f.familyName,
    },
    [PRODUCT_GRP]: {
        prod_reference:   (c: Commodity) => c.commodityReference,
        prod_class:       (c: Commodity) => c.commodityClass,
        prod_description: (c: Commodity) => c.commodityDescription,
    },
    [INVENTION_GRP]: {
        inv_reference: (i: Invention) => i.reference,
        inv_title:     (i: Invention) => i.title,
        inv_date:      (i: Invention) => i.referenceDate,
    },
} as const

const GROUP_ENTITY_GETTER: Record<string, (row: IpRow) => any[]> = {
    [PATENT_FAMILY_GRP]: row => row.families,
    [PATENT_MEMBER_GRP]: row => row.members,
    [TM_GRP]:            row => row.trademarks,
    [TM_FAMILY_GRP]:     row => row.tmFamilies,
    [DESIGN_GRP]:        row => row.designRegistrations,
    [DESIGN_FAMILY_GRP]: row => row.designFamilies,
    [COMP_GRP]:          row => row.compMembers,
    [COMP_FAMILY_GRP]:   row => row.compFamilies,
    [PRODUCT_GRP]:       row => row.products,
    [INVENTION_GRP]:     row => row.inventions,
}

// Build field → group key index for O(1) column dispatch
const fieldToGroup: Record<string, string> = Object.fromEntries(
    Object.entries(_searchableFields).flatMap(([grp, fields]) =>
        Object.keys(fields).map(f => [f, grp])
    )
)

// ─── Entry point ─────────────────────────────────────────────────────────────

export default function ManagementDataWizard() {
    return (
        <AnnuityFieldsProvider>
            <DwSettingsProvider entity={ip_management} defaultGroupBy={GRP_PATENT_MEMBER} allowedValues={ALL_GROUP_BY}>
                <QueriesProvider entity={ip_management}>
                    <DataWizardView />
                </QueriesProvider>
            </DwSettingsProvider>
        </AnnuityFieldsProvider>
    )
}

// ─── Main view ───────────────────────────────────────────────────────────────

function DataWizardView() {
    const { t } = useTranslation()
    const { team: teamName } = useAuth()
    const { team } = useSingleTeamMangement()
    const { hasInnovation, hasAnnuities, hasRevenues } = useRoles()
    const { annuityFields } = useAnnuityFields()
    const { incomeLookup, years } = useIncome()
    const { fxConverter } = useFxContext()
    const currency = team?.currency ?? 'EUR'
    const incomeYears = _(years).takeRight(5).value()
    const incFieldKeys = incomeYears.map(y => `inc_income-${y}`)

    const allFieldKeys = [...Object.keys(fieldToGroup), ...ANN_FIELD_KEYS, ...incFieldKeys]

    const {
        groupBy, setGroupBy,
        searchFields, setSearchFields,
        sortField, setSortField,
        sortOrder, setSortOrder,
    } = useDwSettingsProvider()

    const { showSaveAsModal, showDeleteModal, showOpenModal, selectedQuery } = useQueriesProvider()

    const displayFields: Record<string, string> = {
        // Patent family
        p_familyRef:    t('patentFamilyReference'),
        p_familyName:   t('familyName'),
        p_priorityDate: t('priorityDate'),
        // Patent member
        p_memberRef:    t('familyMemberReference'),
        p_countryCode:  t('countryCode'),
        p_ipType:       t('ipType'),
        p_status:       t('familyMemberStatus'),
        p_appDate:      t('applicationDate'),
        p_appNumber:    t('applicationNumber'),
        p_patentDate:   t('patentDate'),
        p_patentNumber: t('patentNumber'),
        p_expiryDate:   t('expiryDate'),
        // Trademark
        tm_reference:   t('reference'),
        tm_words:       t('words'),
        tm_countryCode: t('countryCode'),
        tm_brandType:   t('brandType'),
        tm_status:      t('status'),
        tm_appDate:     t('applicationDate'),
        tm_appNumber:   t('applicationNumber'),
        tm_regDate:     t('registrationDate'),
        tm_regNumber:   t('registrationNumber'),
        tm_renewalDate: t('renewalDate'),
        // Trademark family
        tm_familyRef:   t('familyReference'),
        tm_familyName:  t('name'),
        // Design registration
        d_reference:    t('reference'),
        d_countryCode:  t('countryCode'),
        d_designType:   t('designType'),
        d_status:       t('status'),
        d_appDate:      t('applicationDate'),
        d_appNumber:    t('applicationNumber'),
        d_regDate:      t('registrationDate'),
        d_regNumber:    t('registrationNumber'),
        d_renewalDate:  t('renewalDate'),
        d_expiryDate:   t('expiryDate'),
        // Design family
        d_familyRef:    t('familyReference'),
        d_familyName:   t('name'),
        // Competitor member
        comp_reference:   t('internalReference'),
        comp_countryCode: t('countryCode'),
        comp_ipType:      t('ipType'),
        comp_status:      t('status'),
        comp_appNumber:   t('applicationNumber'),
        comp_patentNumber:t('patentNumber'),
        comp_expiryDate:  t('expiryDate'),
        // Competitor family
        comp_familyRef:   t('familyReference'),
        comp_familyName:  t('familyName'),
        // Products
        prod_reference:   t('commodityReference'),
        prod_class:       t('commodityClass'),
        prod_description: t('commodityDescription'),
        // Inventions
        inv_reference: t('reference'),
        inv_title:     t('title'),
        inv_date:      t('date'),
        // Annuities
        'ann_cost-centers':    t('costCenters'),
        'ann_fee-due':         t('feeDue'),
        'ann_fees':            t('fees'),
        'ann_instruction-due': t('instructionDue'),
        'ann_last-instruction':t('lastInstruction'),
        'ann_renewals-status': t('renewalStatus'),
        // Income (dynamic — populated below)
        ...Object.fromEntries(incFieldKeys.map(k => [k, `${t('income')} ${k.slice(-4)}`])),
    }

    const availableTags = {
        [`${t('patents')} — ${t('family')}`]:         Object.keys(_searchableFields[PATENT_FAMILY_GRP]),
        [`${t('patents')} — ${t('family-member')}`]:  Object.keys(_searchableFields[PATENT_MEMBER_GRP]),
        ...(hasAnnuities && annuityFields ? { [`${t('patents')} — ${t('annuities')}`]: [...ANN_FIELD_KEYS] } : {}),
        ...(hasInnovation ? { [`${t('patents')} — ${t('inventions')}`]: Object.keys(_searchableFields[INVENTION_GRP]) } : {}),
        ...(hasRevenues && incFieldKeys.length > 0 ? { [`${t('patents')} — ${t('income')}`]: incFieldKeys } : {}),
        [t('trademarks')]:                             Object.keys(_searchableFields[TM_GRP]),
        [`${t('trademarks')} — ${t('family')}`]:      Object.keys(_searchableFields[TM_FAMILY_GRP]),
        [t('designs')]:                                Object.keys(_searchableFields[DESIGN_GRP]),
        [`${t('designs')} — ${t('family')}`]:         Object.keys(_searchableFields[DESIGN_FAMILY_GRP]),
        [t('third-party-patents')]:                    Object.keys(_searchableFields[COMP_GRP]),
        [`${t('third-party-patents')} — ${t('family')}`]: Object.keys(_searchableFields[COMP_FAMILY_GRP]),
        [t('commodity')]:                              Object.keys(_searchableFields[PRODUCT_GRP]),
    }

    const rows = useIpRows(groupBy)
    const sortIndex = searchFields.indexOf(sortField)
    const collator = new Intl.Collator()

    const rowData = _(rows)
        .map(row => ({
            ...row,
            columns: searchFields
                .filter(f => allFieldKeys.includes(f))
                .map(field => {
                    if (fieldToGroup[field]) {
                        const grp = fieldToGroup[field]
                        const accessor = (_searchableFields as any)[grp][field]
                        const entities: any[] = GROUP_ENTITY_GETTER[grp](row)
                        const values = _(entities).flatMap(e => [accessor(e)].flat()).filter(v => v !== undefined && v !== null && v !== '').value()
                        return _.countBy(values, v => String(v))
                    } else if (annuityFields && field.startsWith('ann_')) {
                        const annKey = field.slice(4)
                        const accessor = annuityFields[annKey as keyof AnnuityFields]
                        if (!accessor) return {}
                        const values = _(row.members)
                            .flatMap(m => {
                                const result = accessor(m)
                                return typeof result === 'object' ? Object.keys(result) : [result]
                            })
                            .filter(v => v !== undefined && v !== null && v !== '')
                            .value()
                        return _.countBy(values, v => String(v))
                    } else if (hasRevenues && field.startsWith('inc_')) {
                        const year = parseInt(field.replace('inc_income-', ''))
                        const values = _(row.members)
                            .flatMap(m => row.products.flatMap(c => {
                                const inc = incomeLookup[c.commodityId]?.[m.countryCode]?.[year]
                                return inc ? [fxConverter.convert(inc.amount, inc.currency, currency)] : []
                            }))
                            .filter(v => v !== undefined && v !== null)
                            .value()
                        return _.countBy(values.map(v => currencyFormat.format(v as number)), v => v)
                    }
                    return {}
                })
        }))
        .sort((rowA, rowB) => {
            if (sortIndex < 0) return 0
            const a = Object.keys(rowA.columns[sortIndex] ?? {}).sort()?.[0]
            const b = Object.keys(rowB.columns[sortIndex] ?? {}).sort()?.[0]
            return a === undefined ? 1 : b === undefined ? -1 : collator.compare(a, b) * sortOrder
        })
        .filter(({ columns }) => columns.some(c => _.size(c) > 0))
        .value()

    const totalRow = calculateTotalRows(rowData, searchFields)
    const tableData = { rows: rowData, columnKeys: searchFields, columns: searchFields.map(f => displayFields[f] ?? f) }

    async function exportTable() {
        const excelFile = {
            sheets: [{
                name: t('data-wizard'),
                header: tableData.columns,
                rows: tableData.rows.map(row => row.columns.map(values => Object.keys(values).sort().join(', '))),
                footer: tableData.columnKeys.map(k => asStrings(totalRow[k] ?? {}).join('\n')),
            }],
        }
        return createExcelFile(excelFile, `ip-management-data-${(teamName ?? '').replace(' ', '-')}-${new Date().toISOString().slice(0, 10)}.xlsx`)
    }

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('data-wizard')} | IP Management | Patent Cockpit</title>
        </Helmet>
        <div className="header-row">
            <IpManagementBreadcrumbs label={t('data-wizard')} />
            <div className="flex flex-row gap-2">
                <h2 className="modern-h2 grow">{t('data-wizard')}</h2>
                <LoadingButton onClick={exportTable} className="btn-secondary px-2 py-px font-normal text-sm w-fit">
                    {t('excel-export')}
                </LoadingButton>
            </div>
        </div>
        <div className="main-content">
            <div className="flex flex-row gap-8">
                <div className="grow grid grid-cols-[8rem_1fr] gap-2 items-center">
                    <label htmlFor="ipm-group-by" className="label normal-case">{t('group-by')}</label>
                    <select
                        id="ipm-group-by"
                        className="form-select py-px pl-2 w-fit"
                        value={groupBy}
                        onChange={e => setGroupBy(e.target.value)}
                    >
                        <optgroup label={t('patents')}>
                            <option value={GRP_PATENT_MEMBER}>{t('by-family-member')}</option>
                            <option value={GRP_PATENT_FAMILY}>{t('by-patent-family')}</option>
                        </optgroup>
                        <optgroup label={t('trademarks')}>
                            <option value={GRP_TRADEMARK}>{t('trademark')}</option>
                            <option value={GRP_TRADEMARK_FAMILY}>{t('family')}</option>
                        </optgroup>
                        <optgroup label={t('designs')}>
                            <option value={GRP_DESIGN}>{t('design-registration')}</option>
                            <option value={GRP_DESIGN_FAMILY}>{t('family')}</option>
                        </optgroup>
                        <optgroup label={t('third-party-patents')}>
                            <option value={GRP_COMPETITOR_MEMBER}>{t('competitor-member')}</option>
                            <option value={GRP_COMPETITOR_FAMILY}>{t('family')}</option>
                        </optgroup>
                        <optgroup label={t('commodity')}>
                            <option value={GRP_PRODUCT}>{t('commodity')}</option>
                        </optgroup>
                    </select>

                    <label htmlFor="ipm-fields" className="label normal-case">{t('fields')}</label>
                    <TagList
                        name="ipm-fields"
                        dragable
                        availableTags={availableTags}
                        tags={searchFields}
                        setTags={setSearchFields}
                        tagDisplays={displayFields}
                        placeholder={t('field')}
                    />
                </div>
                <div className="flex lg:flex-col sm:flex-row flex-col lg:items-end sm:items-center gap-2">
                    <h3 className="text-slate-600 whitespace-nowrap">{selectedQuery?.queryName ?? t('unnamed-query')}</h3>
                    <QueryMenu />
                </div>

                {showSaveAsModal && <QueryEditorModal />}
                {showDeleteModal && <QueryDeleteModal />}
                {showOpenModal && <QueryOpenModal />}
            </div>

            {searchFields.length === 0
                ? <div className="text-xl text-gray-700 py-10">{t('data-wizard-empty')}</div>
                : <table className="mt-8 border-spacing-0 border-collapse">
                    <thead className="text-left">
                        <tr>
                            {searchFields.map(searchField =>
                                <th key={searchField} className="sticky bg-white dark:bg-pcx-100 font-semibold text-lg px-0 whitespace-nowrap">
                                    <div className="flex flex-row gap-2 items-center px-1 border-b-2 border-pcx-500 w-full">
                                        {displayFields[searchField] ?? searchField}
                                        <SortButton {...{ searchField, sortField, setSortField, sortOrder, setSortOrder }} />
                                    </div>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rowData.map(row =>
                            <tr key={row.id} className="even:bg-pcx-100 dark:even:bg-pcx-200 last:border-b-2 last:border-pcx-500">
                                {row.columns.map((values, colIdx) =>
                                    <td key={`${row.id}-${colIdx}`} className="px-1 align-top py-0.5">
                                        {renderCell(values, searchFields[colIdx])}
                                    </td>
                                )}
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            {searchFields.map(field =>
                                <td key={field} className="px-1 py-0.5 align-top">
                                    <FooterCell values={totalRow[field] ?? {}} />
                                </td>
                            )}
                        </tr>
                    </tfoot>
                </table>
            }
        </div>
    </>
}

// ─── Cell rendering ──────────────────────────────────────────────────────────

const linkStyle = "underline-link py-0 whitespace-nowrap after:content-[';'] mr-1 last:mr-0 last:after:content-['']"

function renderCell(values: Dictionary<number>, field: string) {
    const keys = _.keys(values).sort()

    if (field === 'p_familyRef') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={familyUrl({ internalReference: ref })}>{ref}</Link>)
    } else if (field === 'p_memberRef') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={memberUrl({ internalReference: ref })}>{ref}</Link>)
    } else if (field === 'tm_reference') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={trademarkUrl({ reference: ref })}>{ref}</Link>)
    } else if (field === 'tm_familyRef') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={trademarkFamilyUrl({ reference: ref })}>{ref}</Link>)
    } else if (field === 'd_reference') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={designRegistrationUrl({ reference: ref })}>{ref}</Link>)
    } else if (field === 'd_familyRef') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={designFamilyUrl({ reference: ref })}>{ref}</Link>)
    } else if (field === 'comp_reference') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={competitorMemberUrl({ internalReference: ref })}>{ref}</Link>)
    } else if (field === 'comp_familyRef') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={competitorFamilyUrl({ internalReference: ref })}>{ref}</Link>)
    } else if (field === 'prod_reference') {
        return keys.map(ref => <Link key={ref} className={linkStyle} to={commodityReferenceUrl({ reference: ref })}>{ref}</Link>)
    } else {
        return keys.join(', ')
    }
}

function asStrings(values: Dictionary<number>) {
    return _(values)
        .toPairs()
        .sortBy([([, c]) => -c, ([v]) => v.toLowerCase()])
        .map(([value, count]) => `${value}: ${count}`)
        .value()
}

function FooterCell({ values }: { values: Dictionary<number> }) {
    return (
        <div className="flex flex-col">
            {asStrings(values).map((s, i) => <span key={i}>{s}</span>)}
        </div>
    )
}
